import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { devDebug } from "@/lib/debug";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { UploadSimple, Trash, FileText, FilePdf } from "@phosphor-icons/react";

/**
 * Upload / manage the "Template PDF SPB (Opsional)" — surfaced inside the
 * Layout Editor page so admins can override the built-in A5 layout without
 * navigating away.
 *
 * Behaviour mirrors the old Settings block (endpoints unchanged) but the UI
 * is a compact card that fits the editor's sidebar/toolbar area.
 *
 * Props:
 *   onChange?: () => void   optional — called after upload/delete so caller
 *                           can trigger a live-PDF refresh.
 */
export default function PdfTemplateCard({ onChange }) {
  const [template, setTemplate] = useState({ exists: false });
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/template");
      setTemplate(r.data || { exists: false });
    } catch (e) {
      // Non-fatal: keep default state so UI shows "layout bawaan"
      devDebug("[PdfTemplateCard] template fetch failed:", e);
    } finally {
      setLoading(false);
    }
    // `api`, `devDebug`, and setters are stable — no reactive inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading same file
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("File harus PDF (.pdf)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/template", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Template PDF berhasil diupload");
      await load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Hapus template PDF? Sistem akan pakai layout bawaan.")) return;
    try {
      await api.delete("/settings/template");
      toast.success("Template dihapus, kembali ke layout bawaan");
      await load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="border border-border bg-card p-4 h-fit" data-testid="pdf-template-card">
      <div className="overline text-xs mb-1 flex items-center gap-2">
        <FilePdf size={14} weight="bold" /> Template PDF (Opsional)
      </div>
      <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
        Upload PDF sendiri (dengan tanda tangan &amp; stempel tetap) untuk
        override layout editor ini. Kosongkan → pakai layout bawaan A5.
      </p>

      <div className="border border-border bg-secondary/30 p-3 font-mono text-xs mb-3">
        {loading ? (
          <span className="text-muted-foreground">Memuat…</span>
        ) : template.exists ? (
          <>
            <div className="overline text-[10px]">Template kustom aktif</div>
            <div className="mt-1 truncate flex items-center gap-1">
              <FileText size={12} />
              {template.filename || "template.pdf"}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              Diupload: {template.updated_at || "-"}
            </div>
          </>
        ) : (
          <>
            <div className="overline text-[10px]">Layout Editor Aktif</div>
            <div className="mt-1 flex items-center gap-1">
              <FileText size={12} /> Menggunakan layout ini
            </div>
          </>
        )}
      </div>

      <div className="flex gap-2">
        {template.exists && (
          <Button
            variant="outline"
            className="rounded-xl text-destructive flex-1"
            onClick={onDelete}
            disabled={uploading}
            data-testid="editor-delete-template-btn"
          >
            <Trash size={14} className="mr-1" /> Hapus
          </Button>
        )}
        <label className="cursor-pointer flex-1">
          <input
            type="file"
            accept="application/pdf"
            onChange={onUpload}
            className="hidden"
            data-testid="editor-upload-template-input"
          />
          <span
            className={`w-full inline-flex items-center justify-center gap-2 px-3 h-9 border font-bold uppercase tracking-wider text-xs ${
              uploading
                ? "bg-secondary text-muted-foreground border-border"
                : "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
            }`}
          >
            <UploadSimple size={14} weight="bold" />
            {uploading ? "Uploading…" : template.exists ? "Ganti" : "Upload PDF"}
          </span>
        </label>
      </div>
    </div>
  );
}
